import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { WorkshopReminder } from "@/pages/Workshops";
import { ResumeSessions } from "@/components/ResumeSessions";
import { ManagerNotes } from "@/components/ManagerNotes";
import { AssignedDrills } from "@/components/AssignedDrills";
import { WatchExplainerButton } from "@/components/ExplainerVideo";
import {
  Flame, Trophy, Target, TrendingUp, Swords, MessageSquareText, Award, Zap, ArrowRight, TrendingDown,
  Loader2, Route, CheckCircle2, Circle, Crown, Sparkles, Medal, GraduationCap, ChevronRight, Lock, Quote
} from "lucide-react";

const COACH_AVATAR = "https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg";
const QUOTES = [
  "The only way to get better is to get the reps in. Let's run one now.",
  "Every objection is a question in disguise. Learn to hear it.",
  "Confidence isn't loud. It's calm, curious and unhurried.",
  "You don't lose the sale at the close. You lose it in the first ten seconds.",
  "Top sellers don't practise until they get it right. They practise until they can't get it wrong.",
];
const ALL_BADGES = ["First Rep", "Getting Serious", "Grinder", "Closer", "7-Day Streak"];

const BADGE_HINTS = { "First Rep": "Finish 1 roleplay", "Getting Serious": "Finish 10 roleplays", "Grinder": "Finish 50 roleplays", "Closer": "Score 90+ on a call", "7-Day Streak": "Practise 7 days running" };

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 1, 0.5, 1] } } };
const card = "bg-white rounded-3xl border border-slate-200/80 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] hover:shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] transition-shadow duration-300 relative overflow-hidden";

function Ring({ value, size = 96, stroke = 9, color = "#10B981", children }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E2E8F0" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (Math.min(100, Math.max(0, value)) / 100) * c }} transition={{ duration: 1.1, ease: "easeOut", delay: 0.3 }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

function Hero({ user, data, navigate }) {
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const quote = QUOTES[new Date().getDate() % QUOTES.length];
  const pct = (data.xp_into_level / data.xp_for_next) * 100;
  return (
    <motion.section variants={item} className={`${card} p-6 sm:p-8 lg:col-span-8`} data-testid="dashboard-hero">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-28 left-1/3 w-80 h-80 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
      <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">{greet}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 mt-1 leading-[1.08]">
            Ready to sharpen up, <span className="text-emerald-600">{user?.name?.split(" ")[0]}</span>?
          </h1>
          <div className="mt-5 flex items-start gap-3 rounded-2xl bg-emerald-50/70 border border-emerald-200/80 p-3.5 max-w-xl" data-testid="hero-quote">
            <img src={COACH_AVATAR} alt="Linden" className="w-11 h-11 rounded-xl object-cover object-top ring-2 ring-white shadow" />
            <div className="min-w-0">
              <p className="text-sm text-slate-800 leading-snug"><Quote className="w-3.5 h-3.5 inline -mt-1 mr-1 text-emerald-600" />{quote}</p>
              <p className="text-[11px] font-mono uppercase tracking-wider text-emerald-700 mt-1">Linden · Head Coach</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/app/roleplay")} className="h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.45)] hover:-translate-y-0.5 transition-all" data-testid="cta-roleplay">
              <Swords className="w-5 h-5 mr-2" /> Start a roleplay
            </Button>
            <Button onClick={() => navigate("/app/coach")} className="h-12 px-5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-all" data-testid="cta-coach">
              <MessageSquareText className="w-5 h-5 mr-2" /> Talk to Linden
            </Button>
            <WatchExplainerButton variant="ghost" label="Watch the tour" className="h-12 px-4 rounded-xl text-slate-600 hover:text-emerald-700 hover:bg-emerald-500/10 font-semibold" testId="dashboard-watch-video-btn" />
          </div>
        </div>
        <div className="flex md:flex-col items-center gap-4 md:pl-6 md:border-l border-slate-200/80" data-testid="level-progress-card">
          <Ring value={pct} size={128} stroke={10}>
            <div className="text-center">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Level</div>
              <div className="text-4xl font-extrabold tracking-tight text-slate-900 font-display leading-none" data-testid="xp-level">{data.level}</div>
            </div>
          </Ring>
          <div className="text-center md:text-center">
            <div className="text-sm font-bold text-slate-900" data-testid="level-title">{data.level_title}</div>
            <div className="text-xs text-slate-500 font-mono mt-0.5" data-testid="xp-counter">{data.xp_into_level} / {data.xp_for_next} XP</div>
            <div className="text-[11px] text-slate-500 mt-1">{data.xp_for_next - data.xp_into_level} XP to <span className="font-semibold text-slate-700">{data.next_level_title}</span></div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function Momentum({ data, navigate }) {
  const goal = Math.max(1, data.weekly_goal);
  const done = Math.min(goal, data.weekly_done);
  const hit = data.weekly_done >= goal;
  return (
    <motion.section variants={item} className={`${card} p-6 lg:col-span-4 flex flex-col`} data-testid="momentum-card">
      <div className="flex items-center justify-between">
        <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Flame className="w-5 h-5 text-amber-500" /> Momentum</h2>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100/80 text-amber-800 border border-amber-200/80" data-testid="stat-streak"><Flame className="w-3.5 h-3.5" /> {data.streak}-day streak</span>
      </div>
      <div className="mt-5">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">This week</div>
            <div className="text-3xl font-extrabold tracking-tight text-slate-900 font-display" data-testid="weekly-progress">{data.weekly_done}<span className="text-slate-400 text-xl"> / {goal}</span></div>
          </div>
          {hit ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100/80 border border-emerald-200/80 rounded-full px-3 py-1" data-testid="weekly-goal-hit"><CheckCircle2 className="w-3.5 h-3.5" /> Goal smashed</span>
               : <span className="text-xs text-slate-500">{goal - data.weekly_done} more to hit your goal</span>}
        </div>
        <div className="mt-3 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${goal}, minmax(0, 1fr))` }} data-testid="weekly-segments">
          {Array.from({ length: goal }).map((_, i) => (
            <motion.div key={i} initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.4 + i * 0.08 }} className={`h-2.5 rounded-full origin-left ${i < done ? "bg-gradient-to-r from-emerald-500 to-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.5)]" : "bg-slate-100"}`} />
          ))}
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[["Calls", data.conversations_completed, Swords, "stat-convos"], ["Win rate", `${data.win_rate}%`, Target, "stat-winrate"], ["Avg score", data.avg_score || "—", TrendingUp, "stat-avgscore"]].map(([l, v, I, t]) => (
          <div key={l} className="rounded-2xl bg-slate-50 border border-slate-100 p-3" data-testid={t}>
            <I className="w-4 h-4 text-slate-400" />
            <div className="text-xl font-extrabold tracking-tight text-slate-900 font-display mt-1.5">{v}</div>
            <div className="text-[11px] text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      <button onClick={() => navigate("/app/leaderboard")} className="mt-auto pt-5 flex items-center justify-between text-left group" data-testid="rank-link">
        <span className="flex items-center gap-2 text-sm text-slate-600"><Crown className="w-4 h-4 text-amber-500" /> Ranked <span className="font-bold text-slate-900">#{data.rank}</span> of {data.total_users}</span>
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
      </button>
    </motion.section>
  );
}

function DailyChallenge({ ch, navigate }) {
  if (!ch) return null;
  return (
    <motion.section variants={item} className={`${card} p-6 md:col-span-1 bg-gradient-to-br from-white via-white to-amber-50/60`} data-testid="daily-challenge-card">
      <div className="flex items-center justify-between">
        <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Today's challenge</h2>
        <span className="text-xs font-mono font-bold text-amber-700">+{ch.xp} XP</span>
      </div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-4">{ch.type}</p>
      <p className="text-xl font-extrabold tracking-tight text-slate-900 font-display mt-1 leading-snug">{ch.title}</p>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{ch.brief}</p>
      <div className="mt-4 flex items-center gap-3">
        {ch.persona_avatar && <img src={ch.persona_avatar} alt="" className="w-10 h-10 rounded-full object-cover object-top ring-2 ring-white shadow" />}
        <div className="text-xs text-slate-500">vs <span className="font-semibold text-slate-800">{ch.persona_title}</span></div>
        <Button size="sm" onClick={() => navigate(`/app/roleplay?persona=${ch.persona_id}`)} className="ml-auto h-9 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="daily-challenge-btn">Take it on <ArrowRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </motion.section>
  );
}

function PlanCard({ plan, navigate }) {
  const next = plan ? plan.steps.filter((s) => !s.done).slice(0, 3) : [];
  const pct = plan ? Math.round((plan.progress.done / Math.max(1, plan.progress.total)) * 100) : 0;
  return (
    <motion.section variants={item} className={`${card} p-6`} data-testid="dashboard-plan-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Route className="w-5 h-5 text-emerald-600" /> Your training plan</h2>
        {plan && <Ring value={pct} size={44} stroke={5}><span className="text-[10px] font-bold text-slate-700" data-testid="dashboard-plan-progress">{plan.progress.done}/{plan.progress.total}</span></Ring>}
      </div>
      {!plan ? (
        <p className="text-sm text-slate-600 mt-4 leading-relaxed">Linden can turn your scores into a six-step path that targets the customer types you struggle with most.</p>
      ) : (
        <>
          <p className="text-sm text-slate-800 mt-4 font-medium leading-snug">{plan.headline}</p>
          <div className="mt-3 space-y-2">
            {next.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-2.5 text-sm rounded-xl px-3 py-2 ${i === 0 ? "bg-emerald-50 border border-emerald-200/80 text-slate-900 font-medium" : "text-slate-600"}`}>
                {i === 0 ? <Zap className="w-4 h-4 text-emerald-600 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}<span className="truncate">{s.title}</span>
              </div>
            ))}
            {next.length === 0 && <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Plan complete — rebuild for your next push.</div>}
          </div>
        </>
      )}
      <Button onClick={() => navigate("/app/plan")} variant="outline" className="mt-5 h-10 rounded-xl border-slate-300 hover:border-emerald-400 hover:text-emerald-700 font-semibold" data-testid="dashboard-plan-btn">
        {plan ? "Open my plan" : "Build my plan"} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </motion.section>
  );
}

function Trend({ recent }) {
  const max = 100;
  return (
    <motion.section variants={item} className={`${card} p-6 flex flex-col`} data-testid="trend-card">
      <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-cyan-600" /> Recent form</h2>
      <p className="text-xs text-slate-500 mt-1">Your last {recent.length || 0} scorecards.</p>
      {recent.length === 0 ? <p className="text-sm text-slate-500 mt-6">Complete a roleplay to start your form line.</p> : (
        <div className="mt-5 flex items-end gap-2 flex-1 min-h-[10rem]" data-testid="trend-bars">
          {recent.map((s, i) => {
            const v = Math.max(4, Math.min(max, s.rating || 0));
            const tone = v >= 70 ? "from-emerald-500 to-emerald-300" : v >= 45 ? "from-cyan-500 to-cyan-300" : "from-amber-500 to-amber-300";
            return (
              <div key={s.id || i} className="flex-1 h-full flex flex-col items-center gap-1.5 group" title={`${s.persona_title}: ${s.rating}`}>
                <span className="text-[10px] font-mono font-bold text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">{s.rating}</span>
                <div className="flex-1 w-full flex items-end rounded-t-lg bg-slate-50">
                  <motion.div initial={{ height: 0 }} animate={{ height: `${v}%` }} transition={{ delay: 0.3 + i * 0.06, duration: 0.6, ease: "easeOut" }} className={`w-full rounded-t-lg bg-gradient-to-t ${tone}`} />
                </div>
                {s.persona_avatar ? <img src={s.persona_avatar} alt="" className="w-6 h-6 rounded-full object-cover object-top ring-1 ring-slate-200" /> : <div className="w-6 h-6" />}
              </div>
            );
          })}
        </div>
      )}
    </motion.section>
  );
}

function Skills({ skills, navigate }) {
  const sorted = [...skills].sort((a, b) => b.avg - a.avg);
  return (
    <motion.section variants={item} className={`${card} p-6`} data-testid="skills-card">
      <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Target className="w-5 h-5 text-emerald-600" /> Skill read-out</h2>
      <p className="text-xs text-slate-500 mt-1">Average across your last five conversations.</p>
      {sorted.length === 0 ? <p className="text-sm text-slate-500 mt-6">Your skill profile appears after your first scorecard.</p> : (
        <div className="mt-4 space-y-2.5">
          {sorted.map((s, i) => (
            <div key={s.skill} data-testid={`skill-${s.skill}`}>
              <div className="flex items-center justify-between text-xs"><span className="capitalize font-medium text-slate-700">{s.skill.replace(/_/g, " ")}</span><span className="font-mono font-bold text-slate-900">{s.avg}</span></div>
              <div className="h-2 mt-1 rounded-full bg-slate-100 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${s.avg}%` }} transition={{ delay: 0.3 + i * 0.06, duration: 0.7 }} className={`h-full rounded-full ${s.avg >= 70 ? "bg-emerald-500" : s.avg >= 45 ? "bg-cyan-500" : "bg-amber-500"}`} />
              </div>
            </div>
          ))}
        </div>
      )}
      {sorted.length > 0 && <button onClick={() => navigate(`/app/roleplay`)} className="mt-4 text-xs font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid="skills-practise-link">Work on {sorted[sorted.length - 1].skill.replace(/_/g, " ")} <ArrowRight className="w-3.5 h-3.5" /></button>}
    </motion.section>
  );
}

function PersonaList({ title, icon: Icon, tone, items, empty, testid, navigate, cta }) {
  return (
    <motion.section variants={item} className={`${card} p-6`} data-testid={testid}>
      <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Icon className={`w-5 h-5 ${tone.icon}`} /> {title}</h2>
      <div className="mt-4 space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-500">{empty}</p>}
        {items.map((w) => (
          <button key={w.id} onClick={() => navigate(`/app/roleplay?persona=${w.id}`)} className="w-full flex items-center gap-3 text-left rounded-2xl border border-transparent hover:border-slate-200 hover:bg-slate-50 p-2 transition-colors group" data-testid={`${testid.replace("-card", "")}-${w.id}`}>
            {w.avatar && <img src={w.avatar} alt="" className="w-11 h-11 rounded-xl object-cover object-top ring-1 ring-slate-200" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-emerald-700">{w.title}</div>
              <div className="text-[11px] text-slate-500">{w.name} · {w.attempts} {w.attempts === 1 ? "call" : "calls"}</div>
            </div>
            <span className={`text-xs font-mono font-bold rounded-full px-2.5 py-1 border ${tone.badge}`}>{w.avg}</span>
            <span className="text-[11px] font-semibold text-slate-400 group-hover:text-emerald-700 hidden xl:inline">{cta}</span>
          </button>
        ))}
      </div>
    </motion.section>
  );
}

function Achievements({ data, navigate }) {
  const earned = new Set(data.badges);
  return (
    <motion.section variants={item} className={`${card} p-6 lg:col-span-12`} data-testid="badges-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> Achievements</h2>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <button onClick={() => navigate("/app/courses")} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-3 py-1 hover:border-emerald-400 hover:text-emerald-700 transition-colors" data-testid="lessons-chip"><GraduationCap className="w-3.5 h-3.5" /> {data.completed_lessons} lessons</button>
          <button onClick={() => navigate("/app/courses")} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-3 py-1 hover:border-emerald-400 hover:text-emerald-700 transition-colors" data-testid="certificates-chip"><Medal className="w-3.5 h-3.5" /> {data.certificates} {data.certificates === 1 ? "certificate" : "certificates"}</button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
        {ALL_BADGES.map((b, i) => {
          const on = earned.has(b);
          return (
            <motion.div key={b} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + i * 0.05 }} title={BADGE_HINTS[b]}
              className={`rounded-2xl p-3 border text-center ${on ? "bg-gradient-to-b from-amber-50 to-white border-amber-200/80 shadow-[0_6px_16px_-8px_rgba(245,158,11,0.5)]" : "bg-slate-50 border-slate-100 opacity-60"}`} data-testid={`badge-${b.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`w-9 h-9 mx-auto rounded-xl flex items-center justify-center ${on ? "bg-amber-500 text-white shadow" : "bg-slate-200 text-slate-400"}`}>{on ? <Trophy className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</div>
              <div className={`text-[11px] font-semibold mt-2 leading-tight ${on ? "text-slate-900" : "text-slate-500"}`}>{b}</div>
              {!on && <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">{BADGE_HINTS[b]}</div>}
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((err) => console.warn("[Dashboard] request failed", err));
    api.get("/plan").then((r) => setPlan(r.data.plan)).catch((err) => console.warn("[Dashboard] request failed", err));
  }, []);

  if (!data) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6" data-testid="dashboard-page">
      <WorkshopReminder />
      <ManagerNotes />
      <AssignedDrills />
      <ResumeSessions max={3} onResume={(s) => navigate(`/app/roleplay?session=${s.id}`)} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        <Hero user={user} data={data} navigate={navigate} />
        <Momentum data={data} navigate={navigate} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <DailyChallenge ch={data.daily_challenge} navigate={navigate} />
        <PlanCard plan={plan} navigate={navigate} />
        <Trend recent={data.recent || []} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skills skills={data.skills || []} navigate={navigate} />
        <PersonaList title="Customers you struggle with" icon={TrendingDown} tone={{ icon: "text-red-500", badge: "bg-red-50 text-red-700 border-red-200" }} items={data.weakest} empty="Run a few roleplays to reveal your weak spots." testid="weakest-card" navigate={navigate} cta="Practise" />
        <PersonaList title="Customers you handle well" icon={Zap} tone={{ icon: "text-emerald-600", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" }} items={data.strongest} empty="Your strengths will show up here." testid="strongest-card" navigate={navigate} cta="Keep sharp" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Achievements data={data} navigate={navigate} />
      </div>
    </motion.div>
  );
}
